import React, { useState, useEffect, useRef } from 'react';
import ReactTouchEvents from 'react-touch-events';
import { useSelector } from 'react-redux';
import {
  MainSliderStyle, Slide, SliderContainer, FirstSlide, SliderProgresBarContainer, SliderProgresBar,
  WholeSiteMask, ArrowDot, Arrow, AnimatedHoverCircle, OnSlideChangeAnimation,
} from './styles';

const navigationWidth = 0.066;

const MainSlider = ({ slides, exceptions }) => {
  const mainSlider = React.createRef();
  const [state, setState] = useState({
    activeSlide: 0,
    translate: 0,
    transition: 1,
    barWidth: slides.length > 1 ? 0 : 100,
    madeAnimationLeft: false,
    madeAnimationRight: false,
    canMove: false,
  });

  const [firstWasClicked, setFirstWasClicked] = useState(false);

  const [move, setMove] = useState(true);

  const firstRender = useRef(true);

  const modalIsOpen = useSelector((store) => store.showModal);

  const {
    translate, transition, activeSlide, barWidth,
    canMove, madeAnimationLeft, madeAnimationRight,
  } = state;

  const notCenterFactor = (array, wind, factor) => {
    if (array[activeSlide + factor].getBoundingClientRect().width !== wind && activeSlide + factor > 0) {
      return (wind * navigationWidth) / 2;
    }
    return null;
  };

  const showMask = (index, slide, array) => {
    if (index !== slide) {
      if (array.length && array.some((x) => x === index)) {
        return false;
      }
      return true;
    }
    return false;
  };

  const makeMovePossible = () => {
    setState({
      ...state,
      canMove: true,
    });
  };

  const stopClick = false;

  const focusGain = (index) => {
    const { children } = mainSlider.current.children[0];
    if (children && activeSlide !== index && move) {
      setMove(false);
      setState({
        ...state,
        activeSlide: index,
      });
      setTimeout(() => {
        setMove(true);
      }, 1000);
    }
  };
  const moveSlide = () => {
    const { children } = mainSlider.current.children[0];
    let item = null;
    for (let i = 0; i < activeSlide; i += 1) {
      item += children[i].getBoundingClientRect().width;
    }
    const wind = window.innerWidth;
    let trans = null;
    if (activeSlide === 0) {
      trans = 0;
    } else if (activeSlide <= children.length - 2) {
      trans = translate - item + (wind / 2) - (children[activeSlide].getBoundingClientRect().width / 2) - mainSlider.current.children[0].getBoundingClientRect().left + notCenterFactor(children, wind, 0);
    } else {
      trans = translate - item + (wind - children[activeSlide].getBoundingClientRect().width) - mainSlider.current.children[0].getBoundingClientRect().left;
    }
    setState({
      ...state,
      translate: trans,
      barWidth: 100 * (activeSlide) / (children.length - 1),
    });
  };
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    moveSlide();
  }, [activeSlide]);

  const slidesList = slides.map((slide, index) => {
    const newKey = `mainSlider_${index}`;
    const newClass = `mainSliderClass_${index}`;
    if (index === 0) {
      return (
        <FirstSlide
          key={newKey}
          className={newClass}
          onAnimationEnd={makeMovePossible}
          onFocus={() => { focusGain(index); }}
          biggerThanOne={slides.length - 1 > 0}
        >
          <WholeSiteMask active={showMask(index, activeSlide, exceptions)} />
          {slide}
        </FirstSlide>
      );
    }
    return (
      <Slide
        key={newKey}
        className={newClass}
        onFocus={() => { focusGain(index); }}
      >
        <WholeSiteMask active={showMask(index, activeSlide, exceptions)} activeLight={index === 1} />
        {slide}
      </Slide>
    );
  });

  const onAnimationEndLeft = () => {
    setState({
      ...state,
      madeAnimationLeft: false,
    });
    setMove(true);
  };

  const onAnimationEndRight = () => {
    setState({
      ...state,
      madeAnimationRight: false,
    });
    setMove(true);
  };

  const nextSlide = (array) => {
    if (!firstWasClicked) {
      setFirstWasClicked(true);
    }
    if (array.length - 1 > activeSlide && move) {
      setMove(false);
      setState({
        ...state,
        activeSlide: activeSlide + 1,
        madeAnimationLeft: true,
        madeAnimationRight: false,
      });
    }
  };

  const prevSlide = () => {
    if (activeSlide > 0 && move) {
      setMove(false);
      setState({
        ...state,
        activeSlide: activeSlide - 1,
        madeAnimationLeft: false,
        madeAnimationRight: true,
      });
    }
  };
  const handleKeyPress = (event) => {
    if (mainSlider.current && !modalIsOpen) {
      const { children } = mainSlider.current.children[0];
      if (event.key === 'ArrowRight') {
        nextSlide(children);
      } else if (event.key === 'ArrowLeft') {
        prevSlide();
      }
    }
  };

  const handleResize = () => {
    if (mainSlider.current && canMove) {
      moveSlide();
    }
  };

  const handleSlideChange = (direction) => {
    const { children } = mainSlider.current.children[0];
    if (direction === 'left') {
      nextSlide(children);
    } else if (direction === 'right') {
      prevSlide();
    }
  };

  useEffect(() => {
    /* eslint-disable no-undef */
    window.addEventListener('resize', handleResize);
    /* eslint-disable no-undef */
    window.addEventListener('keydown', handleKeyPress);
    return () => {
      /* eslint-disable no-undef */
      window.removeEventListener('resize', handleResize);
      /* eslint-disable no-undef */
      window.removeEventListener('keydown', handleKeyPress);
    };
  });

  return (
    <>
      <SliderProgresBarContainer>
        <SliderProgresBar
          translate={translate}
          barWidth={barWidth}
        />
      </SliderProgresBarContainer>
      <ArrowDot isLeft onClick={() => handleSlideChange('right')} isShowing={activeSlide !== 0}>
        {(madeAnimationRight)
              && <OnSlideChangeAnimation onAnimationEnd={onAnimationEndRight} />}
        <AnimatedHoverCircle />
        <Arrow isLeft />
      </ArrowDot>
      <ArrowDot isRight onClick={() => handleSlideChange('left')} isShowing={slidesList.length > 1 && activeSlide !== slides.length - 1 && canMove}>
        {(madeAnimationLeft)
              && <OnSlideChangeAnimation onAnimationEnd={onAnimationEndLeft} />}
        <AnimatedHoverCircle slideIndex={activeSlide} firstWasClicked={firstWasClicked} isRight isShowing={slidesList.length > 1} />
        <Arrow isRight />
      </ArrowDot>
      <ReactTouchEvents
        onSwipe={handleSlideChange}
      >
        <MainSliderStyle
          ref={mainSlider}
        >
          <SliderContainer
            translate={translate}
            transition={transition}
          >
            {slidesList}
          </SliderContainer>
        </MainSliderStyle>
      </ReactTouchEvents>
    </>
  );
};
export default MainSlider;
